import { useResolvedLocale } from '../../lib/hooks/locale.ts';

export type FormatPercentageOptions = {
    locale: string;
    decimals?: number;
    showEmptyIfZero?: boolean;
    emptyText?: string;
    showPlusSignIfPositive?: boolean;
};

/**
 * Pure function to format a number as a percentage string.
 */
export function formatPercentage(
    value: number | string | undefined | null,
    {
        locale,
        decimals = 1,
        emptyText = '-',
        showEmptyIfZero = false,
        showPlusSignIfPositive = false,
    }: FormatPercentageOptions,
): string {
    const cleanValue = Number(value);

    if (Number.isNaN(cleanValue)) {
        return showEmptyIfZero ? emptyText : '0%';
    } else if (cleanValue === 0 && showEmptyIfZero) {
        return emptyText;
    } else {
        return new Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            // no sign on a value that rounds to zero, so -0.2% renders as "0%", not "-0%"
            signDisplay: showPlusSignIfPositive ? 'exceptZero' : 'negative',
        }).format(cleanValue);
    }
}

export type ColorThreshold = {
    min: number;
    color: string;
};

function getThresholdColor(
    value: number | string | undefined | null,
    thresholds: ColorThreshold[],
): string | undefined {
    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
        return undefined;
    }
    const sorted = [...thresholds].toSorted((a, b) => b.min - a.min);
    return sorted.find((t) => numValue >= t.min)?.color;
}

type Props = {
    children: number | string | undefined | null;
    colorThresholds?: ColorThreshold[];
} & Omit<FormatPercentageOptions, 'locale'>;

export function PercentageOrEmpty({
    children,
    decimals = 1,
    emptyText = '-',
    showEmptyIfZero = false,
    showPlusSignIfPositive = false,
    colorThresholds,
}: Props) {
    const locale = useResolvedLocale();

    const formatted = formatPercentage(children, {
        locale,
        decimals,
        emptyText,
        showEmptyIfZero,
        showPlusSignIfPositive,
    });

    if (colorThresholds) {
        const color = getThresholdColor(children, colorThresholds);
        return <span className={color}>{formatted}</span>;
    }

    return <>{formatted}</>;
}
